import type { GetMeQuery } from 'generated/graphql'
import datadog from 'utils/datadog'
import { initializeIntercom, notWinninUserIsAuthenticated, userAuthenticated } from 'utils/gtm'
import localStorageUtils from 'utils/localStorageUtils'

import { STATUS_MESSAGE_ERROR_LOG, WINNIN_EMAIL_DOMAIN } from './use-me.consts'
import type { TMe, TMeStatusMessage, TMeUser } from './use-me.types'

const isUser = (me: TMe | undefined): me is TMeUser => me?.__typename === 'User'

export const isStatusMessage = (me: TMe | undefined): me is TMeStatusMessage => me?.__typename === 'StatusMessage'

const isNonWinninEmail = (email?: string | null): boolean => Boolean(email && !email.includes(WINNIN_EMAIL_DOMAIN))

/**
 * Side effects to run when the `me` query completes. Wired as the query's `onCompleted` (not a
 * `useEffect` watching `data`) so it fires once per completion — `me` lives in the normalized
 * cache as `User:{id}`, and a `useEffect([data])` would re-fire these every time another
 * query/mutation rewrites that entity.
 *
 * - `User`: persist the id and push the GTM events (ported from `winnin-auth` `KeycloakWrapper`).
 * - `StatusMessage`: a *successful* response, so `buildQueryHook`'s errorLog never logs it — log
 *   here instead so the auth-bootstrap failure stays observable. (Network/GraphQL errors are
 *   already logged by the builder via `ME_ERROR_LOG`.)
 */
export const handleMeCompleted = (data: GetMeQuery) => {
  const me = data.me

  if (isUser(me)) {
    localStorageUtils.setObject('id', me.id)
    userAuthenticated(me)
    initializeIntercom()
    if (isNonWinninEmail(me.email)) {
      notWinninUserIsAuthenticated()
    }
    return
  }

  if (isStatusMessage(me)) {
    datadog.dispatchGenericErrorLog({
      log: STATUS_MESSAGE_ERROR_LOG,
      error: new Error(me.title || me.message || 'StatusMessage'),
    })
  }
}
